import React, { createRef, FC, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import {
  Raffle,
  RaffleTicket,
  raffleTicketStatus,
  RaffleTicketStatus,
} from '../../../../../shared/orders/raffle';
import { DataViewItem, DateT, DropdownItemProps, PaginatedDataView } from '../../../../components';
import { StringTranslator, useTranslation } from '../../../../translations';
import { Fn, renderName, useInputState, useNumericInputState } from '../../../../utils';

interface Props {
  readonly raffle: Raffle;
}

export const raffleEntrantsRef = createRef<(() => void) | null>();

export const RaffleEntrants: FC<Props> = ({ raffle }) => {
  const { ts } = useTranslation();
  const [pointsTicket, setPointsTicket] = useState<RaffleTicket | undefined>();
  const [search, setSearch] = useInputState('');
  const [ticketStatus, setTicketStatus] = useInputState('');

  function openSetPointsModal(ticket: RaffleTicket): void {
    setPointsTicket(ticket);
  }

  useEffect(() => {
    raffleEntrantsRef.current?.();
  }, [search, ticketStatus]);

  return (
    <Card id="raffleEntrants">
      {pointsTicket && raffleEntrantsRef.current && (
        <SetPointsModal
          close={() => {
            setPointsTicket(undefined);
          }}
          refresh={raffleEntrantsRef.current}
          ticket={pointsTicket}
        />
      )}
      <CardBody>
        <Row>
          <Col md={8}>
            <Input
              onChange={setSearch}
              placeholder={ts('common.search_prompt')}
              type="text"
              value={search}
            />
          </Col>
          <Col md={4}>
            <Input id="ticketStatus" onChange={setTicketStatus} type="select" value={ticketStatus}>
              <option value="">{ts('all_status')}</option>
              {raffleTicketStatus.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Input>
          </Col>
        </Row>

        <PaginatedDataView<RaffleTicket>
          dataFetcher={async (page) =>
            await api.getRaffleTicketsById(raffle.id, page, search, ticketStatus)
          }
          pageSize={15}
          refreshRef={raffleEntrantsRef}
          renderer={(ticket, refresh) => (
            <DataViewItem
              content={<RaffleTicketRow ticket={ticket} />}
              dropdownItems={getDropdownOptions(ts, ticket, refresh, openSetPointsModal)}
              id={`ticket${ticket.id}`}
              subtitle={`ID: ${ticket.userId}`}
              title={
                <Link to={`/housekeeping/attendees/user/${ticket.user.id}`}>
                  {' '}
                  {renderName(ticket.user)}
                </Link>
              }
            />
          )}
        />
      </CardBody>
    </Card>
  );
};

function getDropdownOptions(
  ts: StringTranslator,
  ticket: RaffleTicket,
  refresh: Fn,
  openSetPointsModal: (ticket: RaffleTicket) => void,
): DropdownItemProps[] {
  const out: DropdownItemProps[] = [
    {
      className: 'ticket-set-points',
      onClick: () => {
        openSetPointsModal(ticket);
      },
      text: 'raffle.set_ticket_points',
    },
  ];

  const ticketReset: DropdownItemProps = {
    className: 'ticket-reset',
    onClick: async () => {
      await api.setRaffleTicket(ticket.raffleId, ticket.userId, 'waiting', ticket.points);
      toast.success(ts('reset_ticket_ticketid_to_entered', ticket.id));
      refresh();
    },
    text: 'raffle.reset_ticket',
  };

  const ticketRescind: DropdownItemProps = {
    className: 'ticket-rescind',
    isDangerous: true,
    onClick: async () => {
      await api.setRaffleTicket(ticket.raffleId, ticket.userId, 'rescinded', ticket.points);
      toast.success(
        ts(
          'rescinded_ticket_ticketid_for_rendernameticketuser',
          ticket.id,
          renderName(ticket.user),
        ),
      );

      refresh();
    },
    text: 'raffle.revoke_ticket',
  };

  const ticketSetWinner: DropdownItemProps = {
    className: 'ticket-set-winner',
    isDangerous: true,
    onClick: async () => {
      await api.setRaffleTicket(ticket.raffleId, ticket.userId, 'winner', ticket.points);
      toast.success(ts('marked_ticket_ticketid_as_a', ticket.id));
      refresh();
    },
    text: 'raffle.reset_ticket_to_winner',
  };

  switch (ticket.status) {
    case 'waiting': {
      out.push(ticketRescind, ticketSetWinner);

      break;
    }

    case 'winner': {
      out.push(ticketReset, ticketRescind);

      break;
    }

    case 'redeemed': {
      out.push(ticketReset, {
        ...ticketSetWinner,
        text: 'raffle.set_ticket_to_winner',
      });

      break;
    }

    case 'rescinded': {
      out.push(ticketReset);
      break;
    }

    case 'deleted': {
      out.push(ticketReset);
      break;
    }
  }

  return out;
}

const RaffleTicketRow: FC<{ readonly ticket: RaffleTicket }> = ({ ticket }) => {
  const { ts } = useTranslation();
  return (
    <div className={`badge-list compact ticket-${ticket.status}`}>
      <RaffleTicketBadge status={ticket.status} />
      {ticket.points > 0 && (
        <Badge color="info">
          {ts('points')}: {ticket.points}
        </Badge>
      )}
      <Badge>
        {ts('common.created_at')}
        <DateT value={ticket.createdAt} />{' '}
      </Badge>
    </div>
  );
};

const RaffleTicketBadge: FC<{ readonly status: RaffleTicketStatus }> = ({ status }) => {
  const { ts } = useTranslation();

  switch (status) {
    case 'waiting': {
      return <Badge color="info">{ts('entered__waiting')}</Badge>;
    }

    case 'winner': {
      return <Badge color="success">{ts('winner')}</Badge>;
    }

    case 'redeemed': {
      return <Badge color="warning">{ts('redeemed')}</Badge>;
    }

    case 'rescinded': {
      return <Badge color="danger">{ts('rescinded')}</Badge>;
    }

    case 'deleted': {
      return <Badge color="danger">{ts('deleted')}</Badge>;
    }

    default: {
      // 9888 is Warning icon
      // eslint-disable-next-line react/jsx-no-literals
      return <Badge color="info">&#9888; {ts('common.unknown')}</Badge>;
    }
  }
};

interface SetPointsModalProps {
  readonly ticket: RaffleTicket;
  readonly refresh: Fn;
  readonly close: Fn;
}

const SetPointsModal: FC<SetPointsModalProps> = ({ ticket, refresh, close }) => {
  const [points, setPoints] = useNumericInputState(ticket.points, true);

  const { ts } = useTranslation();
  async function update(): Promise<void> {
    await api.setRaffleTicket(ticket.raffleId, ticket.userId, ticket.status, points);
    toast.success(ts('updated_ticket_ticketid_to_points', ticket.id, points));
    refresh();
    close();
  }

  return (
    <Modal id="updatePointsModal" isOpen toggle={close}>
      <ModalHeader toggle={close}>{ts('update_ticket_points')}</ModalHeader>
      <ModalBody>
        <p>{ts('set_amount_of_points')}</p>
        <FormGroup>
          <Label for="points">{ts('points')}:</Label>
          <Input id="points" name="points" onChange={setPoints} value={points} />
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="primary" id="updatePoints" onClick={update}>
          {ts('common.update')}
        </Button>{' '}
        <Button color="secondary" onClick={close}>
          {ts('common.cancel')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
